<template>
    <ul ref="articlesRoot" class="filter articles-filter">
        <li class="filter__item" v-if="industryTags.length > 0 && !hideIndustry">
            <label for="industryTagFilter" class="sr-only">{{ dictionary.IndustryDefaultTag }}</label>
            <select id="industryTagFilter" v-model="industryTag" :disabled="isLoading">
                <option value="Industry">{{ dictionary.IndustryDefaultTag }}</option>
                <option v-for="tagItem in industryTags" :key="tagItem.Key" :value="tagItem.Key">{{
                    tagItem.Name
                }}</option>
            </select>
        </li>
        <li class="filter__item" v-if="solutionTags.length > 0 && !hideSolution">
            <label for="solutionTagFilter" class="sr-only">{{ dictionary.SolutionDefaultTag }}</label>
            <select id="solutionTagFilter" v-model="solutionTag" :disabled="isLoading">
                <option value="Solution">{{ dictionary.SolutionDefaultTag }}</option>
                <option v-for="tagItem in solutionTags" :key="tagItem.Key" :value="tagItem.Key">{{
                    tagItem.Name
                }}</option>
            </select>
        </li>
        <li class="filter__item" v-if="locationTags.length > 0 && !hideLocation">
            <label for="locationTagFilter" class="sr-only">{{ dictionary.LocationDefaultTag }}</label>
            <select id="locationTagFilter" v-model="locationTag" :disabled="isLoading">
                <option value="Location">{{ dictionary.LocationDefaultTag }}</option>
                <option v-for="tagItem in locationTags" :key="tagItem.Key" :value="tagItem.Key">{{
                    tagItem.Name
                }}</option>
            </select>
        </li>
        <li class="filter__item" v-if="typeTags.length > 0 && !hideContentType">
            <label for="typeTagFilter" class="sr-only">{{ dictionary.TypeDefaultTag }}</label>
            <select id="typeTagFilter" v-model="typeTag" :disabled="isLoading">
                <option value="Type">{{ dictionary.TypeDefaultTag }}</option>
                <option v-for="tagItem in typeTags" :key="tagItem.Key" :value="tagItem.Key">{{
                    tagItem.Name
                }}</option>
            </select>
        </li>
        <li class="filter__item" v-if="authors.length > 0 && !hideAuthor">
            <label for="typeTagFilter" class="sr-only">{{ dictionary.AuthorDefaultTag }}</label>
            <select id="typeTagFilter" v-model="author" :disabled="isLoading">
                <option value="Author">{{ dictionary.AuthorDefaultTag }}</option>
                <option v-for="author in authors" :key="author.Key" :value="author.Key">{{
                    author.Name
                }}</option>
            </select>
        </li>
    </ul>
    <ul class="articles-page__items">
        <li class="article-item" v-for="project in articles" :key="project.Url">
            <a :href="project.Url" class="article-item__link" @click="saveFilterState">
                <div class="article-item__inner">
                    <div class="article-item__image">
                        <ResponsiveImage v-if="project.ImageForVue != null" :imageModel="project.ImageForVue" />
                    </div>
                    <div class="article-item__text">
                        <div class="article-item__tags" :class="{ 'remove-whitespace': whiteSpace }">
                            <div v-for="industryTag in project.IndustryTags" v-if="project.IndustryTags != null"
                                class="article-item__tags-tag">
                                {{ industryTag }}
                            </div>
                            <div v-for="solutionTag in project.SolutionTags" v-if="project.SolutionTags != null"
                                class="article-item__tags-tag">
                                {{ solutionTag }}
                            </div>
                            <div v-for="locationTag in project.LocationTags" v-if="project.LocationTags != null"
                                class="article-item__tags-tag">
                                {{ locationTag }}
                            </div>
                            <div v-for="typeTag in project.TypeTags" v-if="project.TypeTags != null"
                                class="article-item__tags-type">
                                {{ typeTag }}
                            </div>
                        </div>
                        <h3 class="article-item__headline">
                            {{ project.Headline }}
                        </h3>
                    </div>
                </div>
            </a>
        </li>
    </ul>
    <div class="articles-page__footer">
        <div class="articles-page__pagination" v-if="totalPages > 1">
            <button :disabled="isLoading || page === 1" @click="goToPage(page - 1)"
                class="articles-page__pagination--button articles-page__pagination--button-prev">
            </button>

            <template v-for="p in pagesToShow" :key="p">
                <button v-if="p !== '...'" :disabled="isLoading || page === p" @click="goToPage(p)"
                    class="articles-page__pagination--button"
                    :class="{ 'articles-page__pagination--button-active': page === p }"
                    :aria-current="page === p ? 'page' : null">
                    {{ p }}
                </button>
                <span v-else :key="'ellipsis-' + p" class="articles-page__pagination--ellipsis">...</span>
            </template>

            <button :disabled="isLoading || page === totalPages" @click="goToPage(page + 1)"
                class="articles-page__pagination--button articles-page__pagination--button-next">
            </button>
        </div>
    </div>
</template>

<script>
import { bindRoute } from "./mixins/bindroute.js";
import ResponsiveImage from "./components/responsiveImage.vue";

const serviceUrl = "/umbraco/api/articlesapi/GetArticles";
const defaultIndustryTag = "Industry";
const defaultSolutionTag = "Solution";
const defaultLocationTag = "Location";
const defaultTypeTag = "Type";
const defaultAuthor = "Author";

export default {
    name: "Articles",
    components: {
        ResponsiveImage
    },
    data() {
        return {
            dictionary: [],
            industryTags: [],
            solutionTags: [],
            locationTags: [],
            typeTags: [],
            authors: [],
            culture: "da-DK",
            articles: [],
            pageSize: 9,
            page: 1,
            loadText: "",
            totalArticles: 0,
            isLoading: false,
            hasLoadedInitials: false,
            articlesNodeId: 0,
            isResetting: false,
            whiteSpace: false,
            hideIndustry: false,
            hideSolution: false,
            hideContentType: false,
            hideAuthor: false,
            hideLocation: false,
            windowWidth: window.innerWidth
        };
    },
    mixins: [
        bindRoute({
            industryTag: defaultIndustryTag,
            solutionTag: defaultSolutionTag,
            locationTag: defaultLocationTag,
            typeTag: defaultTypeTag,
            author: defaultAuthor,
            page: 1
        }),
    ],
    watch: {
        industryTag: {
            handler(oldVal, newVal) {
                this.handleFilterPropertyChange(oldVal, newVal);
            }
        },
        solutionTag: {
            handler(oldVal, newVal) {
                this.handleFilterPropertyChange(oldVal, newVal);
            }
        },
        locationTag: {
            handler(oldVal, newVal) {
                this.handleFilterPropertyChange(oldVal, newVal);
            }
        },
        typeTag: {
            handler(oldVal, newVal) {
                this.handleFilterPropertyChange(oldVal, newVal);
            }
        },
        author: {
            handler(oldVal, newVal) {
                this.handleFilterPropertyChange(oldVal, newVal);
            }
        }
    },
    mounted() {
        this.articlesNodeId = this.$parent.articlesNodeId;
        this.pageSize = this.$parent.pageSize;
        this.whiteSpace = this.$parent.whiteSpace == "True";
        this.loadText = this.$parent.loadText;
        this.hideIndustry = this.$parent.hideIndustry == "True";
        this.hideSolution = this.$parent.hideSolution == "True";
        this.hideContentType = this.$parent.hideContentType == "True";
        this.hideAuthor = this.$parent.hideAuthor == "True";
        this.hideLocation = this.$parent.hideLocation == "True";
        window.addEventListener('resize', this.updateWindowWidth);

        // Restore saved filters
        const saved = sessionStorage.getItem('articleFilters');
        if (saved) {
            const filters = JSON.parse(saved);
            this.isResetting = true; // prevent watch handlers firing fetchs mid-restore
            this.industryTag = filters.industryTag ?? defaultIndustryTag;
            this.solutionTag = filters.solutionTag ?? defaultSolutionTag;
            this.locationTag = filters.locationTag ?? defaultLocationTag;
            this.typeTag = filters.typeTag ?? defaultTypeTag;
            this.author = filters.author ?? defaultAuthor;
            this.page = filters.page ?? 1;
            sessionStorage.removeItem('articleFilters'); // clean up after restoring
            this.$nextTick(() => { this.isResetting = false; });
        }

        this.$router.isReady().then(() => {
            this.getInitialArticles();
        }).catch(error => {
            console.log(error);
        });
    },
    unmounted() {
        window.removeEventListener('resize', this.updateWindowWidth);
    },
    methods: {
        updateWindowWidth() {
            this.windowWidth = window.innerWidth;
        },
        handleFilterPropertyChange(oldVal, newVal) {
            if (oldVal !== newVal) {
                if (this.hasLoadedInitials && !this.isResetting) {
                    this.filterChanged();
                }
            }
        },
        getArticles(amountToSkip, pageSize) {
            const vm = this,
                languageCode = document.querySelector("html").getAttribute("lang");
            vm.toggleSpinner(true);
            var object = {
                articlesNodeId: vm.articlesNodeId,
                languageCode: languageCode,
                amountToSkip: amountToSkip,
                pageSize: pageSize,
                loadText: vm.loadText
            };

            if (vm.industryTag != defaultIndustryTag) {
                object.industryTag = vm.industryTag;
            }

            if (vm.solutionTag != defaultSolutionTag) {
                object.solutionTag = vm.solutionTag;
            }

            if (vm.locationTag != defaultLocationTag) {
                object.locationTag = vm.locationTag;
            }

            if (vm.typeTag != defaultTypeTag) {
                object.typeTag = vm.typeTag;
            }

            if (vm.author != defaultAuthor) {
                object.author = vm.author;
            }

            fetch(serviceUrl + "?" + new URLSearchParams(object))
                .then((response) => response.json())
                .then((data) => {
                    vm.articles = data.Items;
                    vm.industryTags = data.Facets.IndustryTags;
                    vm.solutionTags = data.Facets.SolutionTags;
                    vm.locationTags = data.Facets.LocationTags;
                    vm.typeTags = data.Facets.TypeTags;
                    vm.authors = data.Facets.Authors;
                    vm.dictionary = data.Dictionary;
                    vm.totalArticles = data.TotalItems;
                    vm.hasLoadedInitials = true;
                    vm.toggleSpinner(false);
                    vm.isResetting = false;

                    const urlParams = new URLSearchParams(window.location.search);
                    const filterKeys = ['industryTag', 'solutionTag', 'locationTag', 'typeTag'];
                    filterKeys.forEach(key => urlParams.delete(key));

                    const newSearch = urlParams.toString();
                    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
                    window.history.replaceState({}, document.title, newUrl);

                })
                .catch((error) => {
                    console.log(error);
                    vm.toggleSpinner(false);
                });
        },
        goToPage(pageNumber) {
            if (!this.isLoading && pageNumber > 0 && pageNumber <= this.totalPages) {
                this.page = pageNumber;

                if (this.$refs.articlesRoot) {
                    this.$refs.articlesRoot.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }

                const amountToSkip = this.pageSize * (pageNumber - 1);

                this.getArticles(amountToSkip, this.pageSize);
            }
        },
        getInitialArticles() {
            // Get all cases up to, and including, the current page
            const initialPageSize = this.pageSize * this.page;
            this.getArticles(0, initialPageSize);
        },
        filterChanged() {
            this.articles = [];
            this.totalArticles = 0;
            this.page = 1;
            this.getArticles(0, this.pageSize);
        },
        toggleSpinner: function (isLoading) {
            this.isLoading = isLoading;
            const bodyElement = document.querySelector("body");
            if (isLoading) {
                bodyElement.classList.add("is-loading");
            } else {
                bodyElement.classList.remove("is-loading");
            }
        },
        saveFilterState() {
            sessionStorage.setItem('articleFilters', JSON.stringify({
                industryTag: this.industryTag,
                solutionTag: this.solutionTag,
                locationTag: this.locationTag,
                typeTag: this.typeTag,
                author: this.author,
                page: this.page,
            }));
        },
    },
    computed: {
        isMobile() {
            // Adjust the breakpoint (e.g., 768px) as needed for your design
            return this.windowWidth <= 768;
        },
        totalPages() {
            return Math.ceil(this.totalArticles / this.pageSize);
        },
        pagesToShow() {
            const pages = [];
            const total = this.totalPages;
            const current = this.page;
            const pageWindow = 1;

            if (total <= 1) {
                return [];
            }

            // 🌟 NEW LOGIC: If on desktop, show ALL pages and return immediately
            if (!this.isMobile) {
                const allPages = [];
                for (let i = 1; i <= total; i++) {
                    allPages.push(i);
                }
                return allPages;
            }
            // ------------------------------------------------------------------

            // Mobile/Ellipsis Logic (Only runs if this.isMobile is true)

            const pageSet = new Set();
            pageSet.add(1);
            pageSet.add(total);

            for (let i = current - pageWindow; i <= current + pageWindow; i++) {
                if (i > 1 && i < total) {
                    pageSet.add(i);
                }
            }

            const sortedPages = Array.from(pageSet).sort((a, b) => a - b);

            let lastPage = 0;
            for (const p of sortedPages) {
                // Ellipsis only added for mobile
                if (this.isMobile && lastPage !== 0 && p > lastPage + 1) {
                    pages.push('...');
                }
                pages.push(p);
                lastPage = p;
            }

            return pages;
        },
        hasFilterApplied() {
            return this.industryTag != defaultIndustryTag ||
                this.solutionTag != defaultSolutionTag ||
                this.locationTag != defaultLocationTag ||
                this.typeTag != defaultTypeTag
                || this.author != defaultAuthor;
        }
    },
};
</script>